import { FC } from "react";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import { GitbookUnifiedProps } from "shared/ui/markdown/components/gitbook";

export const GITBOOK_EMBED_TYPES = {
  YOUTUBE: "youtube",
  VIMEO: "vimeo",
  LOOM: "loom",
  IFRAME: "iframe",
} as const;

type GitbookEmbedType = ValueOfObjectFields<typeof GITBOOK_EMBED_TYPES>;

/**
 * Detect the type of embed based on the URL
 */
function getEmbedType(url: string): GitbookEmbedType {
  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    return GITBOOK_EMBED_TYPES.YOUTUBE;
  } else if (url.includes("vimeo.com")) {
    return GITBOOK_EMBED_TYPES.VIMEO;
  } else if (url.includes("loom.com")) {
    return GITBOOK_EMBED_TYPES.LOOM;
  }

  return GITBOOK_EMBED_TYPES.IFRAME; // Generic iframe fallback
}

/**
 * Convert various URL formats to embeddable URLs
 */
function getEmbedUrl(url: string, type: GitbookEmbedType): string {
  switch (type) {
    case GITBOOK_EMBED_TYPES.YOUTUBE: {
      // Convert youtube.com/watch?v=ID or youtu.be/ID to embed format
      const youtubeId = url.match(
        /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/
      )?.[1];
      return youtubeId ? `https://www.youtube.com/embed/${youtubeId}` : url;
    }
    case GITBOOK_EMBED_TYPES.VIMEO: {
      // Convert vimeo.com/ID to embed format
      const vimeoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
      return vimeoId ? `https://player.vimeo.com/video/${vimeoId}` : url;
    }
    case GITBOOK_EMBED_TYPES.LOOM: {
      // Convert loom.com/share/ID to embed format so it works within an iframe
      const loomId = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/)?.[1];
      return loomId ? `https://www.loom.com/embed/${loomId}` : url;
    }
    default:
      return url;
  }
}

export const GitbookEmbedComponent: FC<GitbookUnifiedProps> = ({ url }) => {
  if (!url) {
    return (
      <div className="my-4 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
        <p className="font-medium">❌ Embed Error</p>
        <p className="text-sm">No URL provided for embed</p>
      </div>
    );
  }

  const embedType = getEmbedType(url);
  const embedUrl = getEmbedUrl(url, embedType);

  const getEmbedConfig = (type: GitbookEmbedType) => {
    switch (type) {
      case GITBOOK_EMBED_TYPES.YOUTUBE:
        return {
          title: "YouTube Video",
          aspectRatio: "aspect-video", // 16:9
          allow:
            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
        };
      case GITBOOK_EMBED_TYPES.VIMEO:
        return {
          title: "Vimeo Video",
          aspectRatio: "aspect-video",
          allow: "autoplay; fullscreen; picture-in-picture",
        };
      case GITBOOK_EMBED_TYPES.LOOM:
        return {
          title: "Loom Video",
          aspectRatio: "aspect-video",
          allow: "autoplay; fullscreen; picture-in-picture",
        };
      default:
        return {
          title: "Embedded Content",
          aspectRatio: "aspect-video",
          allow: "",
        };
    }
  };

  const config = getEmbedConfig(embedType);

  return (
    <div className="my-6">
      <div
        className={`relative w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-100 ${config.aspectRatio}`}
      >
        <iframe
          src={embedUrl}
          title={config.title}
          className="absolute inset-0 h-full w-full"
          frameBorder="0"
          allow={config.allow}
          allowFullScreen
          loading="lazy"
        />
      </div>

      {/* Optional caption or fallback link */}
      <div className="mt-2 text-center">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-gray-600 underline hover:text-blue-600"
        >
          View original:{" "}
          {embedType.charAt(0).toUpperCase() + embedType.slice(1)}
        </a>
      </div>
    </div>
  );
};
